import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Tv, LayoutDashboard, Users, Settings as SettingsIcon, User as UserIcon, MessageSquare } from "lucide-react";

const NavLink = ({ to, icon: Icon, label, active }) => (
  <Link
    to={to}
    className={`flex items-center gap-2 px-3 h-10 text-xs uppercase tracking-[0.2em] font-bold border ${
      active
        ? "bg-white text-black border-white"
        : "border-white/10 text-white/60 hover:text-white hover:border-white/30"
    } transition-colors rounded-sm`}
    data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <Icon className="w-3.5 h-3.5" />
    <span className="hidden md:inline">{label}</span>
  </Link>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const isManager = ["floor_manager", "sales_manager", "gsm"].includes(user?.role);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col">
      <header className="border-b border-white/10 bg-[#0A0A0A] sticky top-0 z-30">
        <div className="px-4 md:px-6 h-14 flex items-center gap-2 md:gap-4">
          <button
            onClick={() => nav("/board")}
            className="flex items-center gap-2 mr-2"
            data-testid="brand-logo"
          >
            <span className="w-2.5 h-2.5 bg-[#34C759] rounded-full" />
            <span className="font-display text-lg font-black uppercase tracking-widest">THE BE BACK</span>
          </button>
          <nav className="flex items-center gap-1.5 flex-1">
            <NavLink to="/board" icon={LayoutDashboard} label="Board" active={loc.pathname === "/board"} />
            <NavLink to="/me" icon={UserIcon} label="My Day" active={loc.pathname === "/me"} />
            <NavLink to="/chat" icon={MessageSquare} label="Chat" active={loc.pathname === "/chat"} />
            {isManager && <NavLink to="/roster" icon={Users} label="Roster" active={loc.pathname === "/roster"} />}
            {isManager && <NavLink to="/settings" icon={SettingsIcon} label="Settings" active={loc.pathname === "/settings"} />}
            <NavLink to="/tower" icon={Tv} label="Tower" active={loc.pathname === "/tower"} />
          </nav>
          <div className="flex items-center gap-2 md:gap-3">
            <div className="hidden md:flex flex-col items-end leading-tight">
              <span className="text-xs font-bold">{user?.display_name || user?.name}</span>
              <span className="text-[10px] uppercase tracking-widest text-white/40">{user?.role?.replace("_", " ")}</span>
            </div>
            <Button
              onClick={logout}
              variant="ghost"
              size="sm"
              className="text-white/50 hover:text-white hover:bg-white/5 rounded-sm"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1">{children}</main>
    </div>
  );
}
